import React from 'react';

const OrderSummary = ({ cart, summary, summaryPricing = null, showPaymentStatus = false }) => {
  // Use summary if provided, otherwise use cart
  const data = summary || cart;

  // Calculate totals — prefer summaryPricing (real Shiprocket cost) over cart placeholder
  const calculateFinalTotal = () => {
    const subtotal = data?.subtotal || data?.subTotal || 0;
    const shipping = summaryPricing?.shippingCost
      ?? data?.shipping?.cost
      ?? data?.pricing?.shippingCost
      ?? data?.shippingCost
      ?? 0;
    const gst = summaryPricing?.gst?.total
      ?? data?.gstCalculation?.totalGst
      ?? data?.gst
      ?? 0;
    return subtotal + shipping + gst;
  };

  const calculateDiscount = (mrp, price) => {
    if (!mrp || !price || mrp <= price) return 0;
    return Math.round(((mrp - price) / mrp) * 100);
  };

  // ✅ Get payment status styling
  const getPaymentStatusStyle = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'paid':
      case 'success':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'refunded':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Check if data is empty
  if (!data || (!data.items && !data.cartItems) || (data.items?.length === 0 && data.cartItems?.length === 0)) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
        <p className="text-gray-600 text-center py-8">Your cart is empty</p>
      </div>
    );
  }

  // Get items from either cart or summary
  const items = data.items || data.cartItems || [];
  const totalItems = data.totalItems || items.length;
  
  // Get pricing info (handle both structures)
  const subtotal = data.subtotal || data.subTotal || 0;   // selling price (already discounted from MRP)
  const totalMrp = data.totalMrp || 0;
  const bulkDiscount = data.bulkDiscount || 0;
  const discount = totalMrp > 0 ? totalMrp - subtotal : (data.discount || 0); // MRP − selling price
  // Prefer real Shiprocket pricing from summaryPricing over cart placeholder (0)
const shippingCost = summaryPricing?.shippingCost
    ?? data.shipping?.cost
    ?? data.pricing?.shippingCost
    ?? data.shippingCost
    ?? 0;
  const gst = summaryPricing?.gst?.total
    ?? data.gstCalculation?.totalGst
    ?? data.gst
    ?? 0;

  // Get payment info
  const paymentStatus = data.paymentStatus || data.payment?.status;
  const paymentMethod = data.paymentMethod || data.payment?.method;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b bg-gray-50">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold">Order Summary</h2>
            <p className="text-sm text-gray-600 mt-1">
              {totalItems} {totalItems === 1 ? 'item' : 'items'}
            </p>
          </div>
          
          {/* ✅ Show payment status badge if enabled and available */}
          {showPaymentStatus && paymentStatus && (
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPaymentStatusStyle(paymentStatus)}`}>
              {paymentStatus}
            </span>
          )}
        </div>

        {/* ✅ Show order ID if available (for order summary, not cart) */}
        {data.orderId && (
          <p className="text-sm text-gray-600 mt-2">
            Order ID: <span className="font-medium text-gray-900">{data.orderId}</span>
          </p>
        )}
      </div>

      {/* Cart Items List */}
      <div className="p-4 border-b max-h-96 overflow-y-auto">
        <h3 className="font-medium mb-3 text-gray-700">Items in {data.orderId ? 'Order' : 'Cart'}</h3>
        <div className="space-y-3">
          {items.map((item, index) => {
            const uniqueKey = `${item.productId}-${item.color || 'default'}-${index}-${item.quantity}`;
            
            return (
              <div 
                key={uniqueKey}
                className="flex gap-3 pb-3 border-b border-gray-100 last:border-0"
              >
                {/* Product Image */}
                <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                  <img
                    src={item.thumbnailUrl || item.imageUrl || '/images/placeholder.png'}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 truncate">
                    {item.name}
                  </h4>
                  
                  <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                    {item.color && (
                      <p>Color: {item.color}</p>
                    )}
                    {item.material && (
                      <p>Material: {item.material}</p>
                    )}
                    <p>Qty: {item.quantity}</p>
                  </div>

                  {/* Price */}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm font-semibold text-gray-900">
                      ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                    </span>
                    
                    {item.mrp > item.price && (
                      <>
                        <span className="text-xs text-gray-500 line-through">
                          ₹{(item.mrp * item.quantity).toLocaleString('en-IN')}
                        </span>
                        <span className="text-xs text-green-600 font-medium">
                          {calculateDiscount(item.mrp, item.price)}% off
                        </span>
                      </>
                    )}
                  </div>

                  {/* Bulk Discount Badge */}
                  {item.isBulkItem && (
                    <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                      Bulk Discount
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Price Breakdown */}
      <div className="p-4 space-y-3 border-b">
        <h3 className="font-medium text-gray-700">Price Details</h3>
        
        <div className="space-y-2 text-sm">
          {/* Subtotal = selling price (item discounts already applied) */}
          <div className="flex justify-between text-gray-700">
            <span>Subtotal</span>
            <span className="font-medium">₹{subtotal.toLocaleString('en-IN')}</span>
          </div>

          <div className="flex justify-between text-gray-600">
            <span>Shipping Charges</span>
            <span className="font-medium">
              {shippingCost > 0
                ? `₹${shippingCost.toLocaleString('en-IN')}`
                : <span className="text-green-600">Free</span>
              }
            </span>
          </div>

          {gst > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>GST</span>
              <span className="font-medium">₹{gst.toLocaleString('en-IN')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Total Amount */}
      <div className="p-4 bg-gray-50">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold text-gray-900">Total Amount</span>
          <span className="text-2xl font-bold text-gray-900">
            ₹{calculateFinalTotal().toLocaleString('en-IN')}
          </span>
        </div>

        {/* Savings Display */}
        {discount > 0 && (
          <p className="text-sm text-green-600 mt-2">
            You saved ₹{discount.toLocaleString('en-IN')} on this order
          </p>
        )}

        {/* ✅ Payment Method Display */}
        {showPaymentStatus && paymentMethod && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Payment Method:</span>
              <span className="font-medium text-gray-900 capitalize">
                {paymentMethod === 'COD' ? 'Cash on Delivery' : paymentMethod}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderSummary;